import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem
} from '@mui/material';
import { useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useDebouncedEffect } from '../hooks/useDebouncedEffect';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { fetchRegulationCategories, fetchRegulations, selectRegulationCategories } from '../redux/regulationSlice';

const pages = [
  { title: 'Home', path: '/' },
  { title: 'About', path: '/about' },
  { title: 'History', path: '/history' },
  { title: 'What is Gymkhana', path: '/what-is-gymkhana' },
  { title: 'Events', path: '/events' },
  { title: 'Documents', path: '/regulations' },
  { title: 'Gallery', path: '/gallery' },
  { title: 'Contact Us', path: '/contact' },
];

function Navbar() {
  const dispatch = useDispatch();
  const [anchorElNav, setAnchorElNav] = useState(null);
  const categories = useSelector(selectRegulationCategories);
  const [anchorElRegulations, setAnchorElRegulations] = useState(null);

  useDebouncedEffect(() => {
    dispatch(fetchRegulationCategories())
    dispatch(fetchRegulations())
  }, 50, [dispatch]);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleOpenRegulationsMenu = (event) => {
    setAnchorElRegulations(event.currentTarget);
  };

  const handleCloseRegulationsMenu = () => {
    setAnchorElRegulations(null);
  };

  return (
    <AppBar position="sticky" sx={{ backgroundColor: '#0a192f' }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Logo/Brand - visible on desktop */}
          <Box component={RouterLink} to="/" sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            <img
              src="/logo512.png"
              alt="AAGC"
              style={{ width: 120, height: 60, objectFit: 'contain' }}
            />
            <img
              src="/fia-logo.png"
              alt="FIA"
              style={{ width: 70, height: 50, objectFit: 'contain' }}
            />
          </Box>

          {/* Mobile menu */}
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {pages.map((page) => {
                if (page.title === 'Contact Us') {
                  return (
                    <MenuItem
                      key={page.path}
                      onClick={handleCloseNavMenu}
                      component={RouterLink}
                      to={page.path}
                      sx={{
                        mx: 1, my: 0.5,
                        border: '1px solid rgba(213,170,77,0.6)',
                        borderRadius: '50px',
                        color: '#d5aa4d',
                        gap: 1,
                        '&:hover': { bgcolor: 'rgba(213,170,77,0.08)' },
                      }}
                    >
                      <EditNoteIcon sx={{ fontSize: '1.1rem' }} />
                      <Typography textAlign="center">Contact Us</Typography>
                    </MenuItem>
                  );
                }
                if (page.title === 'Documents') {
                  return (
                    <div key={page.path}>
                      <MenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenRegulationsMenu(e);
                        }}
                      >
                        <Typography textAlign="center">
                          {page.title}
                          <KeyboardArrowDownIcon />
                        </Typography>
                      </MenuItem>
                      <Menu
                        anchorEl={anchorElRegulations}
                        open={Boolean(anchorElRegulations)}
                        onClose={handleCloseRegulationsMenu}
                        sx={{
                          '& .MuiPaper-root': {
                            boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                          }
                        }}
                      >
                        <MenuItem
                          component={RouterLink}
                          to={page.path}
                          onClick={handleCloseRegulationsMenu}
                        >
                          <Typography textAlign="center">All Documents</Typography>
                        </MenuItem>
                        {categories.map((category) => (
                          <MenuItem
                            key={category.id}
                            component={RouterLink}
                            to={`${page.path}/${category.id}`}
                            onClick={handleCloseRegulationsMenu}
                          >
                            <Typography textAlign="center">{category.name}</Typography>
                          </MenuItem>
                        ))}
                      </Menu>
                    </div>
                  );
                }
                return (
                  <MenuItem
                    key={page.path}
                    onClick={handleCloseNavMenu}
                    component={RouterLink}
                    to={page.path}
                  >
                    <Typography textAlign="center">{page.title}</Typography>
                  </MenuItem>
                );
              })}
            </Menu>
          </Box>

          {/* Logo/Brand - visible on mobile */}
          <Box component={RouterLink} to="/" sx={{ display: { xs: 'flex', md: 'none' }, flexGrow: 1, alignItems: 'center' }}>
            <img
              src="/logo512.png"
              alt="AAGC"
              style={{ width: 90, height: 40, objectFit: 'contain' }}
            />
            <img
              src="/fia-logo.png"
              alt="FIA"
              style={{ width: 55, height: 35, objectFit: 'contain' }}
            />
          </Box>

          {/* Desktop menu */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}>
            {pages.map((page) => {
              if (page.title === 'Documents') {
                return (
                  <Box key={page.path} sx={{ position: 'relative' }}>
                    <Button
                      onClick={handleOpenRegulationsMenu}
                      sx={{ my: 2, color: 'white', display: 'flex', alignItems: 'center' }}
                    >
                      {page.title}
                      <KeyboardArrowDownIcon />
                    </Button>
                    <Menu
                      anchorEl={anchorElRegulations}
                      open={Boolean(anchorElRegulations)}
                      onClose={handleCloseRegulationsMenu}
                      sx={{
                        mt: '45px',
                        '& .MuiPaper-root': {
                          boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                        }
                      }}
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                      keepMounted
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                    >
                      <MenuItem
                        component={RouterLink}
                        to={page.path}
                        onClick={handleCloseRegulationsMenu}
                      >
                        <Typography textAlign="center">All Documents</Typography>
                      </MenuItem>
                      {categories.map((category) => (
                        <MenuItem
                          key={category.id}
                          component={RouterLink}
                          to={`${page.path}/${category.id}`}
                          onClick={handleCloseRegulationsMenu}
                        >
                          <Typography textAlign="center">{category.name}</Typography>
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box>
                );
              }
              if (page.title === 'Contact Us') {
                return (
                  <Button
                    key={page.path}
                    component={RouterLink}
                    to={page.path}
                    onClick={handleCloseNavMenu}
                    startIcon={<EditNoteIcon sx={{ fontSize: '1rem' }} />}
                    sx={{
                      my: 2,
                      ml: 2,
                      px: 2.5,
                      py: 0.75,
                      color: '#0a0a0a',
                      background: 'linear-gradient(135deg, #f5c842 0%, #d4932a 100%)',
                      borderRadius: '50px',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      letterSpacing: '0.05em',
                      textTransform: 'none',
                      boxShadow: '0 0 12px rgba(213,170,77,0.35), 0 2px 8px rgba(0,0,0,0.3)',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0, left: '-75%',
                        width: '50%', height: '100%',
                        background: 'linear-gradient(120deg, transparent, rgba(255,255,255,0.35), transparent)',
                        transition: 'left 0.5s ease',
                      },
                      '&:hover': {
                        background: 'linear-gradient(135deg, #fdd835 0%, #e6a020 100%)',
                        boxShadow: '0 0 22px rgba(245,200,66,0.55), 0 4px 16px rgba(0,0,0,0.3)',
                        transform: 'translateY(-1px)',
                        '&::before': { left: '125%' },
                      },
                    }}
                  >
                    Contact Us
                  </Button>
                );
              }
              return (
                <Button
                  key={page.path}
                  component={RouterLink}
                  to={page.path}
                  onClick={handleCloseNavMenu}
                  sx={{ my: 2, color: 'white', display: 'block' }}
                >
                  {page.title}
                </Button>
              );
            })}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default Navbar;
